import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

/**
 * FilePreviewModal Component
 * Comprehensive full-screen multi-format file viewer with Cloudflare R2 presigned streaming.
 * Handles:
 *  - Images (High-res zoom in/out, fit to window, full-screen canvas)
 *  - Spreadsheets (Live interactive CSV/TSV table grid parser)
 *  - PDFs & Office Docs (Embedded document viewer with fallback)
 *  - Videos & Audio (HTML5 player with playback controls and waveforms)
 *  - Code & Plain Text (Live fetch, monospaced line numbers, copy code)
 *  - Designer Files (.PSD, .AI, .FIG, .SKETCH, .XD, .EPS) with Creative Studio Cards
 *  - Compressed Archives (.ZIP, .RAR, .7Z) with Archive Inspection Cards
 *  - Mobile & Apple formats (.HEIC, .PAGES, .NUMBERS) with device format guides
 *  - Cloud-Linked Resources (Google Drive, Figma, Loom, Dropbox)
 */
export default function FilePreviewModal({
    isOpen,
    file,
    previewUrl,
    onClose,
    onDownload,
    onArchive,
    onRestore,
}) {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [textContent, setTextContent] = useState(null);
    const [loadingText, setLoadingText] = useState(false);
    const [textError, setTextError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [csvViewMode, setCsvViewMode] = useState('table'); // 'table' | 'raw'
    const [tryAiPdf, setTryAiPdf] = useState(false);

    // Derive normalized extension
    const ext = useMemo(() => {
        if (!file?.name) return (file?.type || '').toUpperCase();
        const parts = file.name.split('.');
        return parts.length > 1 ? parts.pop().toUpperCase() : (file?.type || '').toUpperCase();
    }, [file]);

    const mime = (file?.mimeType || '').toLowerCase();

    // Cloud-linked external services (Asana-style external attachments)
    const isCloudLink = useMemo(() => {
        const url = previewUrl || file?.publicUrl || '';
        return file?.isExternal || /drive\.google\.com|docs\.google\.com|figma\.com|loom\.com|dropbox\.com|box\.com|1drv\.ms|sharepoint\.com/i.test(url);
    }, [previewUrl, file]);

    const cloudMeta = useMemo(() => {
        const url = previewUrl || file?.publicUrl || '';
        if (/drive\.google\.com|docs\.google\.com/i.test(url)) {
            return { provider: 'Google Drive', icon: 'add_to_drive', color: 'text-amber-500', bg: 'bg-amber-500/10' };
        }
        if (/figma\.com/i.test(url)) {
            return { provider: 'Figma', icon: 'draw', color: 'text-purple-500', bg: 'bg-purple-500/10' };
        }
        if (/loom\.com/i.test(url)) {
            return { provider: 'Loom Video', icon: 'videocam', color: 'text-rose-500', bg: 'bg-rose-500/10' };
        }
        if (/dropbox\.com/i.test(url)) {
            return { provider: 'Dropbox', icon: 'cloud_download', color: 'text-blue-500', bg: 'bg-blue-500/10' };
        }
        if (/1drv\.ms|sharepoint\.com/i.test(url)) {
            return { provider: 'OneDrive / SharePoint', icon: 'cloud', color: 'text-sky-600', bg: 'bg-sky-600/10' };
        }
        return { provider: 'External Cloud Resource', icon: 'link', color: 'text-primary', bg: 'bg-primary/10' };
    }, [previewUrl, file]);

    // Format category detectors
    const isVideo = ['MP4', 'WEBM', 'MOV', 'MKV', 'AVI', 'M4V', 'OGV'].includes(ext) || mime.startsWith('video/');
    const isAudio = ['MP3', 'WAV', 'OGG', 'M4A', 'AAC', 'FLAC'].includes(ext) || mime.startsWith('audio/');
    const isImage = ['PNG', 'JPG', 'JPEG', 'WEBP', 'AVIF', 'GIF', 'SVG', 'BMP', 'ICO'].includes(ext) || mime.startsWith('image/');
    const isPdf = ext === 'PDF' || mime === 'application/pdf';
    const isSpreadsheet = ['CSV', 'TSV'].includes(ext) || mime === 'text/csv';
    const isOffice = ['DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX'].includes(ext);
    const isArchive = ['ZIP', 'RAR', '7Z', 'TAR', 'GZ', 'BZ2'].includes(ext);
    const isDesigner = ['PSD', 'AI', 'FIG', 'SKETCH', 'XD', 'EPS', 'INDD'].includes(ext);
    const isAppleFormat = ['HEIC', 'HEIF', 'PAGES', 'NUMBERS', 'KEY'].includes(ext);
    const isTextOrCode = !isSpreadsheet && (['TXT', 'MD', 'JSON', 'JS', 'JSX', 'TS', 'TSX', 'HTML', 'CSS', 'PY', 'XML', 'YAML', 'YML', 'SQL', 'SH', 'ENV', 'LOG'].includes(ext) || mime.startsWith('text/'));

    // Reset zoom and text state when file changes or modal opens
    useEffect(() => {
        setZoom(1);
        setTextContent(null);
        setTextError(null);
        setCopied(false);
        setCsvViewMode('table');
        setTryAiPdf(false);

        if (isOpen && (isTextOrCode || isSpreadsheet) && previewUrl) {
            setLoadingText(true);
            fetch(previewUrl)
                .then((res) => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.text();
                })
                .then((txt) => {
                    setTextContent(txt);
                    setLoadingText(false);
                })
                .catch((err) => {
                    console.warn('[FilePreviewModal] Could not fetch text content:', err);
                    setTextError('Could not render live preview.');
                    setLoadingText(false);
                });
        }
    }, [isOpen, file?.id, previewUrl, isTextOrCode, isSpreadsheet]);

    // Keyboard listener for Escape (fullscreen exit or close)
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isFullscreen) {
                    setIsFullscreen(false);
                } else {
                    onClose?.();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isFullscreen, onClose]);

    // Parse CSV / TSV tabular data on the fly
    const parsedCsvData = useMemo(() => {
        if (!isSpreadsheet || !textContent) return null;
        const lines = textContent.trim().split(/\r?\n/).filter(Boolean);
        if (!lines.length) return null;

        const delimiter = ext === 'TSV' ? '\t' : ',';
        const parseLine = (line) => {
            const result = [];
            let cur = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                    if (inQuotes && line[i + 1] === '"') {
                        cur += '"';
                        i++;
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (char === delimiter && !inQuotes) {
                    result.push(cur.trim());
                    cur = '';
                } else {
                    cur += char;
                }
            }
            result.push(cur.trim());
            return result;
        };

        const headers = parseLine(lines[0]);
        // Limit to 150 rows for ultra fast DOM rendering
        const rows = lines.slice(1, 151).map(parseLine);
        return { headers, rows, totalRows: lines.length - 1 };
    }, [isSpreadsheet, textContent, ext]);

    if (!isOpen || !file) return null;

    const handleCopyCode = () => {
        if (!textContent) return;
        navigator.clipboard.writeText(textContent);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const modalContent = (
        <div
            className={`
                fixed inset-0 z-[99999] flex items-center justify-center
                ${isFullscreen
                    ? 'p-0 w-screen h-screen bg-black/95'
                    : 'p-3 md:p-6 bg-[#0b1c30]/85 backdrop-blur-md animate-in fade-in duration-200'
                }
            `}
        >
            <div
                className={`
                    file-preview-modal relative flex flex-col bg-white dark:bg-[#141c2b] shadow-2xl overflow-hidden
                    ${isFullscreen
                        ? 'w-screen h-screen rounded-none border-none'
                        : 'w-full max-w-6xl max-h-[92vh] rounded-2xl border border-[#c1c6d7] dark:border-[#344258]'
                    }
                `}
            >
                {/* ── Modal Header Bar (Always completely visible & above all viewports) ── */}
                <div className="h-14 px-4 sm:px-6 border-b border-[#c1c6d7] dark:border-[#344258] flex items-center justify-between bg-white dark:bg-[#141c2b] flex-shrink-0 z-30 select-none">
                    {/* Left: File icon, Name & Metadata */}
                    <div className="flex items-center gap-3 truncate min-w-0 mr-3">
                        <div className="w-9 h-9 rounded-lg bg-[#eff4ff] dark:bg-[#1a2538] flex items-center justify-center flex-shrink-0 text-[#0070F3]">
                            <span className="material-symbols-outlined text-xl">
                                {isCloudLink
                                    ? cloudMeta.icon
                                    : isVideo
                                    ? 'movie'
                                    : isAudio
                                    ? 'audiotrack'
                                    : isImage
                                    ? 'image'
                                    : isPdf
                                    ? 'picture_as_pdf'
                                    : isSpreadsheet
                                    ? 'table_chart'
                                    : isDesigner
                                    ? 'palette'
                                    : isArchive
                                    ? 'folder_zip'
                                    : (file.icon || 'description')
                                }
                            </span>
                        </div>
                        <div className="truncate min-w-0">
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold text-sm text-[#0b1c30] dark:text-[#eaf1ff] truncate" title={file.name}>
                                    {file.name}
                                </h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#eff4ff] dark:bg-[#1a2538] text-[#0070F3] border border-[#0070F3]/20 flex-shrink-0">
                                    {ext}
                                </span>
                            </div>
                            <p className="text-[11px] text-[#727786] dark:text-[#9aa5bc] truncate">
                                {file.project || 'Internal Asset'} • {file.version || 'v1.0'} • {file.size || 'Unknown size'} • {file.date || 'Recent'}
                            </p>
                        </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Open in New Tab if URL exists */}
                        {(previewUrl || file.publicUrl) && (
                            <button
                                type="button"
                                onClick={() => window.open(previewUrl || file.publicUrl, '_blank')}
                                className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#c1c6d7] dark:border-[#344258] text-[#414754] dark:text-[#9aa5bc] hover:bg-[#eff4ff] dark:hover:bg-[#212f45] text-xs font-semibold transition-colors"
                                title="Open raw file in new browser tab"
                            >
                                <span className="material-symbols-outlined text-base">open_in_new</span>
                                <span className="hidden md:inline">Open URL</span>
                            </button>
                        )}

                        {/* Archive or Restore button */}
                        {file.status === 'archived' ? (
                            onRestore && (
                                <button
                                    type="button"
                                    onClick={() => onRestore(file)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 border border-[#006a61]/40 text-[#006a61] dark:text-[#89f5e7] bg-[#e0f5f2]/40 dark:bg-[#123630]/40 rounded-lg text-xs font-semibold hover:bg-[#e0f5f2] transition-colors"
                                    title="Restore file to active folder"
                                >
                                    <span className="material-symbols-outlined text-base">unarchive</span>
                                    <span className="hidden sm:inline">Restore</span>
                                </button>
                            )
                        ) : (
                            onArchive && (
                                <button
                                    type="button"
                                    onClick={() => onArchive(file)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 border border-[#ba1a1a]/30 text-[#ba1a1a] rounded-lg text-xs font-semibold hover:bg-[#ffdad6]/30 transition-colors"
                                    title="Soft-archive file"
                                >
                                    <span className="material-symbols-outlined text-base">archive</span>
                                    <span className="hidden sm:inline">Archive</span>
                                </button>
                            )
                        )}

                        {/* Download button */}
                        <button
                            type="button"
                            onClick={() => onDownload?.(file)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-[#0070F3] hover:bg-[#0059c5] text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
                            title="Download file securely from Cloudflare R2"
                        >
                            <span className="material-symbols-outlined text-base">download</span>
                            <span className="hidden sm:inline">Download</span>
                        </button>

                        {/* Fullscreen Toggle */}
                        <button
                            type="button"
                            onClick={() => setIsFullscreen((prev) => !prev)}
                            className={`p-1.5 rounded-lg border transition-colors ${
                                isFullscreen
                                    ? 'bg-[#0070F3]/15 border-[#0070F3] text-[#0070F3] shadow-inner'
                                    : 'border-[#c1c6d7] dark:border-[#344258] text-[#414754] dark:text-[#9aa5bc] hover:bg-[#eff4ff] dark:hover:bg-[#212f45]'
                            }`}
                            title={isFullscreen ? 'Exit Full Screen (Esc)' : 'Enter Full Screen'}
                        >
                            <span className="material-symbols-outlined text-lg">
                                {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                            </span>
                        </button>

                        {/* Close button */}
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-[#727786] hover:bg-[#eff4ff] dark:hover:bg-[#212f45] transition-colors"
                            title="Close preview (Esc)"
                        >
                            <span className="material-symbols-outlined text-xl">close</span>
                        </button>
                    </div>
                </div>

                {/* ── Modal Body: Adaptive Document Surface ── */}
                <div
                    className={`
                        relative flex-1 w-full flex flex-col items-center justify-center bg-[#f4f7fc] dark:bg-[#0c131d] overflow-hidden
                        ${isFullscreen ? 'p-0 h-[calc(100vh-56px)]' : 'min-h-[480px] max-h-[78vh] p-4 md:p-6'}
                    `}
                >
                    {/* 1. CLOUD-LINKED ATTACHMENT (Google Drive, Figma, Loom, Dropbox) */}
                    {isCloudLink ? (
                        <div className="w-full max-w-lg bg-white dark:bg-[#141c2b] p-8 rounded-2xl shadow-xl border border-[#c1c6d7] dark:border-[#344258] flex flex-col items-center text-center animate-in zoom-in-95 duration-150">
                            <div className={`w-20 h-20 rounded-2xl ${cloudMeta.bg} ${cloudMeta.color} flex items-center justify-center mb-5 shadow-sm`}>
                                <span className="material-symbols-outlined text-5xl">
                                    {cloudMeta.icon}
                                </span>
                            </div>
                            <span className="text-[11px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 mb-2">
                                {cloudMeta.provider} Attachment
                            </span>
                            <h4 className="font-bold text-lg text-[#0b1c30] dark:text-[#eaf1ff] mb-2 px-4">
                                {file.name}
                            </h4>
                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc] mb-6 max-w-sm">
                                This resource is stored as an integrated cloud link. Click below to open and collaborate directly in {cloudMeta.provider}.
                            </p>
                            <button
                                type="button"
                                onClick={() => window.open(previewUrl || file.publicUrl, '_blank')}
                                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0070F3] hover:bg-[#0059c5] text-white rounded-xl text-xs font-bold shadow-md transition-transform active:scale-98"
                            >
                                <span className="material-symbols-outlined text-base">open_in_new</span>
                                Open in {cloudMeta.provider}
                            </button>
                        </div>
                    ) : /* 2. VIDEO VIEWER */
                    isVideo && previewUrl ? (
                        <div className="w-full h-full flex flex-col items-center justify-center p-2 sm:p-4">
                            <video
                                key={previewUrl}
                                src={previewUrl}
                                controls
                                autoPlay
                                playsInline
                                className={`
                                    max-w-full rounded-xl shadow-2xl border border-[#c1c6d7] dark:border-[#344258] bg-black
                                    ${isFullscreen ? 'max-h-[calc(100vh-90px)]' : 'max-h-[64vh]'}
                                `}
                            />
                            {['AVI', 'MKV'].includes(ext) && (
                                <p className="text-[11px] text-[#727786] dark:text-[#9aa5bc] mt-2 text-center">
                                    Note: Browsers require external codecs for {ext} files. If playback fails, please use the Download button.
                                </p>
                            )}
                        </div>
                    ) : /* 3. AUDIO VIEWER */
                    isAudio && previewUrl ? (
                        <div className="w-full max-w-lg bg-white dark:bg-[#141c2b] p-8 rounded-2xl shadow-xl border border-[#c1c6d7] dark:border-[#344258] flex flex-col items-center text-center">
                            <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-[#0070F3] to-[#00c2cb] flex items-center justify-center text-white shadow-lg mb-5 animate-pulse">
                                <span className="material-symbols-outlined text-5xl">
                                    graphic_eq
                                </span>
                            </div>
                            <h4 className="font-bold text-base text-[#0b1c30] dark:text-[#eaf1ff] mb-1">
                                {file.name}
                            </h4>
                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc] mb-6">
                                Audio Asset • {ext} Format • {file.size}
                            </p>
                            <audio
                                key={previewUrl}
                                src={previewUrl}
                                controls
                                autoPlay
                                className="w-full"
                            />
                        </div>
                    ) : /* 4. IMAGE VIEWER WITH FULL SCREEN SCALING & ZOOM */
                    isImage && previewUrl ? (
                        <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
                            {/* Floating Zoom Bar */}
                            <div className="absolute top-3 right-4 z-20 flex items-center gap-1 bg-white/90 dark:bg-[#141c2b]/90 backdrop-blur-md p-1 rounded-xl shadow-lg border border-[#c1c6d7] dark:border-[#344258]">
                                <button
                                    type="button"
                                    onClick={() => setZoom((z) => Math.max(0.25, Number((z - 0.25).toFixed(2))))}
                                    className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs font-bold transition-colors"
                                    title="Zoom Out"
                                >
                                    <span className="material-symbols-outlined text-sm">zoom_out</span>
                                </button>
                                <span className="text-[11px] font-bold px-2 text-[#0b1c30] dark:text-[#eaf1ff] min-w-[42px] text-center select-none">
                                    {Math.round(zoom * 100)}%
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))))}
                                    className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs font-bold transition-colors"
                                    title="Zoom In"
                                >
                                    <span className="material-symbols-outlined text-sm">zoom_in</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setZoom(1)}
                                    className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 text-[10px] font-semibold text-[#0070F3] px-1.5 transition-colors"
                                    title="Reset Zoom to 100%"
                                >
                                    Fit
                                </button>
                            </div>

                            {/* Image Canvas with transparency checkerboard background */}
                            <div className="w-full h-full flex items-center justify-center overflow-auto p-2 sm:p-4">
                                <img
                                    src={previewUrl}
                                    alt={file.name}
                                    style={{
                                        transform: `scale(${zoom})`,
                                        transformOrigin: 'center center',
                                    }}
                                    className={`
                                        object-contain rounded-lg shadow-md transition-transform duration-150
                                        ${isFullscreen ? 'max-h-[calc(100vh-80px)] max-w-full' : 'max-h-[64vh] max-w-full'}
                                    `}
                                />
                            </div>
                        </div>
                    ) : /* 5. INTERACTIVE SPREADSHEET / CSV DATA TABLE */
                    isSpreadsheet && previewUrl ? (
                        <div className={`w-full h-full flex flex-col ${isFullscreen ? 'rounded-none border-0' : 'max-w-6xl rounded-xl border border-[#c1c6d7] dark:border-[#344258]'} bg-white dark:bg-[#141c2b] shadow-2xl overflow-hidden`}>
                            {/* Table Controls Header */}
                            <div className="px-4 py-2.5 bg-slate-50 dark:bg-[#1a2538] border-b border-[#c1c6d7] dark:border-[#344258] flex items-center justify-between text-xs">
                                <div className="flex items-center gap-3">
                                    <span className="font-semibold text-[#0b1c30] dark:text-[#eaf1ff] flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-base text-emerald-600">table_view</span>
                                        Spreadsheet Data ({ext})
                                    </span>
                                    {parsedCsvData && (
                                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                                            {parsedCsvData.totalRows} records
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex bg-slate-200 dark:bg-slate-800 p-0.5 rounded-lg text-[11px]">
                                        <button
                                            type="button"
                                            onClick={() => setCsvViewMode('table')}
                                            className={`px-2.5 py-1 rounded font-medium transition-colors ${csvViewMode === 'table' ? 'bg-white dark:bg-[#141c2b] text-[#0070F3] shadow-xs' : 'text-slate-500'}`}
                                        >
                                            Table Grid
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCsvViewMode('raw')}
                                            className={`px-2.5 py-1 rounded font-medium transition-colors ${csvViewMode === 'raw' ? 'bg-white dark:bg-[#141c2b] text-[#0070F3] shadow-xs' : 'text-slate-500'}`}
                                        >
                                            Raw Data
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleCopyCode}
                                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-[11px] font-medium transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-xs">
                                            {copied ? 'check' : 'content_copy'}
                                        </span>
                                        {copied ? 'Copied' : 'Copy CSV'}
                                    </button>
                                </div>
                            </div>

                            {/* Viewport: Table vs Raw Text */}
                            <div className={`p-0 overflow-auto ${isFullscreen ? 'h-[calc(100vh-110px)]' : 'h-[62vh]'}`}>
                                {loadingText ? (
                                    <div className="flex items-center justify-center h-full text-slate-400 text-xs py-16">
                                        <span className="material-symbols-outlined animate-spin mr-2">sync</span>
                                        Parsing spreadsheet data...
                                    </div>
                                ) : csvViewMode === 'table' && parsedCsvData ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-xs border-collapse">
                                            <thead className="bg-slate-100 dark:bg-slate-800/80 sticky top-0 z-10 border-b border-slate-200 dark:border-slate-700">
                                                <tr>
                                                    <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-12 text-center border-r border-slate-200 dark:border-slate-700">
                                                        #
                                                    </th>
                                                    {parsedCsvData.headers.map((h, i) => (
                                                        <th key={i} className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap border-r border-slate-200 dark:border-slate-700">
                                                            {h || `Column ${i + 1}`}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                {parsedCsvData.rows.map((row, rIdx) => (
                                                    <tr key={rIdx} className="hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors">
                                                        <td className="px-3 py-2 text-[10px] font-mono text-slate-400 text-center bg-slate-50/50 dark:bg-slate-900/30 border-r border-slate-200 dark:border-slate-700">
                                                            {rIdx + 1}
                                                        </td>
                                                        {row.map((cell, cIdx) => (
                                                            <td key={cIdx} className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap font-mono text-[11px] border-r border-slate-100 dark:border-slate-800">
                                                                {cell}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <pre className="p-4 font-mono text-xs text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap break-words">
                                        {textContent}
                                    </pre>
                                )}
                            </div>
                        </div>
                    ) : /* 6. PDF VIEWER */
                    isPdf && previewUrl ? (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                            <iframe
                                src={previewUrl}
                                title={file.name}
                                className={`
                                    w-full border-0 bg-white shadow-md
                                    ${isFullscreen ? 'h-full rounded-none' : 'h-[68vh] rounded-xl border border-[#c1c6d7] dark:border-[#344258]'}
                                `}
                            />
                        </div>
                    ) : /* 7. TEXT / CODE VIEWER */
                    isTextOrCode && previewUrl ? (
                        <div className={`w-full h-full flex flex-col ${isFullscreen ? 'max-w-none rounded-none border-0' : 'max-w-5xl rounded-xl border border-[#212f45]'} bg-[#090d16] overflow-hidden shadow-2xl`}>
                            <div className="px-4 py-2.5 bg-[#121927] border-b border-[#212f45] flex items-center justify-between text-xs text-slate-400">
                                <span className="font-mono text-slate-300 font-semibold flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-sm text-[#0070F3]">code</span>
                                    {file.name}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCopyCode}
                                        disabled={!textContent}
                                        className="flex items-center gap-1 px-2.5 py-1 rounded bg-[#212f45] hover:bg-[#2c3d5a] text-slate-200 text-xs font-medium transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-xs">
                                            {copied ? 'check' : 'content_copy'}
                                        </span>
                                        {copied ? 'Copied!' : 'Copy Code'}
                                    </button>
                                </div>
                            </div>
                            <div className={`p-4 overflow-auto font-mono text-xs text-slate-200 leading-relaxed ${isFullscreen ? 'h-[calc(100vh-105px)]' : 'h-[60vh]'}`}>
                                {loadingText ? (
                                    <div className="flex items-center justify-center h-full text-slate-400">
                                        <span className="material-symbols-outlined animate-spin mr-2">sync</span>
                                        Loading text contents...
                                    </div>
                                ) : textError ? (
                                    <div className="text-center py-12 text-rose-400">
                                        <p>{textError}</p>
                                        <button
                                            type="button"
                                            onClick={() => window.open(previewUrl, '_blank')}
                                            className="mt-3 px-3 py-1 bg-slate-800 rounded text-slate-200 text-xs"
                                        >
                                            View RAW in Browser
                                        </button>
                                    </div>
                                ) : (
                                    <pre className="whitespace-pre-wrap break-words">{textContent}</pre>
                                )}
                            </div>
                        </div>
                    ) : /* 8. OFFICE DOCUMENTS (WORD, EXCEL, POWERPOINT) */
                    isOffice && previewUrl ? (
                        <div className={`w-full h-full flex flex-col items-center justify-center ${isFullscreen ? 'p-0' : 'max-w-5xl'}`}>
                            <div className="w-full mb-2 px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 flex items-center justify-between text-xs text-amber-900 dark:text-amber-200">
                                <span className="flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-sm text-amber-600">article</span>
                                    Office Document Viewer ({ext})
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`, '_blank')}
                                        className="font-bold underline hover:text-amber-700"
                                    >
                                        Open Full View
                                    </button>
                                    <span>•</span>
                                    <button
                                        type="button"
                                        onClick={() => onDownload?.(file)}
                                        className="font-bold underline hover:text-amber-700"
                                    >
                                        Download File
                                    </button>
                                </div>
                            </div>
                            <iframe
                                src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                                title={file.name}
                                className={`
                                    w-full border border-[#c1c6d7] dark:border-[#344258] bg-white shadow-md
                                    ${isFullscreen ? 'h-[calc(100vh-120px)] rounded-none' : 'h-[60vh] rounded-xl'}
                                `}
                            />
                        </div>
                    ) : /* 9. CREATIVE & DESIGNER FILES (.AI, .PSD, .FIG, .SKETCH, .XD, .EPS) */
                    isDesigner ? (
                        <div className="w-full max-w-xl bg-white dark:bg-[#141c2b] p-8 rounded-2xl shadow-xl border border-[#c1c6d7] dark:border-[#344258] flex flex-col items-center text-center animate-in zoom-in-95 duration-150">
                            {/* Software Badge */}
                            <div
                                className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-5 text-white font-black text-2xl shadow-lg ${
                                    ext === 'PSD'
                                        ? 'bg-[#001d34] border-2 border-[#31a8ff] text-[#31a8ff]'
                                        : ext === 'AI' || ext === 'EPS'
                                        ? 'bg-[#330000] border-2 border-[#ff9a00] text-[#ff9a00]'
                                        : ext === 'FIG'
                                        ? 'bg-[#1e1e1e] border-2 border-[#a259ff] text-[#a259ff]'
                                        : ext === 'SKETCH'
                                        ? 'bg-[#fdad00]/15 border-2 border-[#fdad00] text-[#fdad00]'
                                        : 'bg-[#ff61f6]/15 border-2 border-[#ff61f6] text-[#ff61f6]'
                                }`}
                            >
                                {ext}
                            </div>
                            <span className="text-[11px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 mb-2">
                                Creative Studio Asset
                            </span>
                            <h4 className="font-bold text-lg text-[#0b1c30] dark:text-[#eaf1ff] mb-2 px-4">
                                {file.name}
                            </h4>
                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc] mb-6 max-w-sm">
                                {ext === 'AI'
                                    ? 'Adobe Illustrator vector project file. Download to edit layers and vector paths, or view in desktop app.'
                                    : ext === 'PSD'
                                    ? 'Adobe Photoshop multilayer artwork. Contains raw raster layers, masks, and smart objects.'
                                    : ext === 'FIG'
                                    ? 'Figma component and frame asset. Download to import into your Figma workspace.'
                                    : `High-fidelity ${ext} design source asset.`
                                }
                            </p>

                            <div className="flex gap-2 mb-6">
                                <span className="px-3 py-1 bg-[#eff4ff] dark:bg-[#1a2538] text-[#0070F3] rounded-full text-xs font-bold">
                                    {ext} Source
                                </span>
                                <span className="px-3 py-1 bg-[#e0f5f2] dark:bg-[#123630] text-[#006a61] dark:text-[#89f5e7] rounded-full text-xs font-bold">
                                    {file.size}
                                </span>
                            </div>

                            <div className="w-full flex flex-col gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => onDownload?.(file)}
                                    className="w-full flex items-center justify-center gap-2 py-3 bg-[#0070F3] hover:bg-[#0059c5] text-white rounded-xl text-xs font-bold shadow-md transition-colors"
                                >
                                    <span className="material-symbols-outlined text-base">download</span>
                                    Download Source File ({file.size})
                                </button>
                                {ext === 'AI' && previewUrl && (
                                    <button
                                        type="button"
                                        onClick={() => window.open(previewUrl, '_blank')}
                                        className="w-full flex items-center justify-center gap-2 py-2 border border-[#c1c6d7] dark:border-[#344258] text-[#414754] dark:text-[#9aa5bc] hover:bg-[#eff4ff] dark:hover:bg-[#212f45] rounded-xl text-xs font-semibold transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-base">picture_as_pdf</span>
                                        Inspect PDF Preview Stream
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : /* 10. COMPRESSED ARCHIVES (ZIP, RAR, 7Z) */
                    isArchive ? (
                        <div className="w-full max-w-md bg-white dark:bg-[#141c2b] p-8 rounded-2xl shadow-xl border border-[#c1c6d7] dark:border-[#344258] flex flex-col items-center text-center animate-in zoom-in-95 duration-150">
                            <div className="w-20 h-20 rounded-2xl bg-[#6366f1]/15 text-[#6366f1] flex items-center justify-center mb-4">
                                <span className="material-symbols-outlined text-5xl">
                                    folder_zip
                                </span>
                            </div>
                            <h4 className="font-bold text-base text-[#0b1c30] dark:text-[#eaf1ff] mb-1">
                                {file.name}
                            </h4>
                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc] mb-6 max-w-xs">
                                Compressed archive bundle ({ext}). Contains archived project files, packages, or production builds.
                            </p>
                            <button
                                type="button"
                                onClick={() => onDownload?.(file)}
                                className="w-full flex items-center justify-center gap-2 py-3 bg-[#6366f1] hover:bg-[#4f46e5] text-white rounded-xl text-xs font-bold shadow-md transition-colors"
                            >
                                <span className="material-symbols-outlined text-base">download</span>
                                Download Archive ({file.size})
                            </button>
                        </div>
                    ) : /* 11. APPLE / MOBILE FORMATS (HEIC, PAGES, NUMBERS) */
                    isAppleFormat ? (
                        <div className="w-full max-w-md bg-white dark:bg-[#141c2b] p-8 rounded-2xl shadow-xl border border-[#c1c6d7] dark:border-[#344258] flex flex-col items-center text-center animate-in zoom-in-95 duration-150">
                            <div className="w-20 h-20 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 flex items-center justify-center mb-4">
                                <span className="material-symbols-outlined text-5xl">
                                    phone_iphone
                                </span>
                            </div>
                            <h4 className="font-bold text-base text-[#0b1c30] dark:text-[#eaf1ff] mb-1">
                                {file.name}
                            </h4>
                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc] mb-6 max-w-xs">
                                {ext === 'HEIC' || ext === 'HEIF'
                                    ? 'Apple High Efficiency Image format from iOS. Download to view in Photos or standard image editor.'
                                    : `Apple iWork document (${ext}). Download to open on macOS or iPadOS.`
                                }
                            </p>
                            <button
                                type="button"
                                onClick={() => onDownload?.(file)}
                                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0070F3] hover:bg-[#0059c5] text-white rounded-xl text-xs font-bold shadow-md transition-colors"
                            >
                                <span className="material-symbols-outlined text-base">download</span>
                                Download File ({file.size})
                            </button>
                        </div>
                    ) : (
                        /* 12. GRACEFUL FALLBACK FOR BINARY / UNPREVIEWABLE FILES */
                        <div className="w-full max-w-md bg-white dark:bg-[#141c2b] p-8 rounded-2xl shadow-xl border border-[#c1c6d7] dark:border-[#344258] flex flex-col items-center text-center">
                            <div className="w-20 h-20 rounded-2xl bg-[#0070F3]/10 text-[#0070F3] flex items-center justify-center mb-4">
                                <span className="material-symbols-outlined text-5xl">
                                    {file.icon || 'description'}
                                </span>
                            </div>
                            <h4 className="font-bold text-base text-[#0b1c30] dark:text-[#eaf1ff] mb-1">
                                {file.name}
                            </h4>
                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc] mb-4 max-w-xs">
                                {file.description || 'Production file recorded in ERP and stored securely in Cloudflare R2.'}
                            </p>
                            <div className="flex gap-2 mb-6">
                                <span className="px-3 py-1 bg-[#eff4ff] dark:bg-[#1a2538] text-[#0070F3] rounded-full text-xs font-bold">
                                    {ext} File
                                </span>
                                <span className="px-3 py-1 bg-[#e0f5f2] dark:bg-[#123630] text-[#006a61] dark:text-[#89f5e7] rounded-full text-xs font-bold">
                                    {file.size}
                                </span>
                            </div>
                            <div className="w-full flex flex-col gap-2">
                                <button
                                    type="button"
                                    onClick={() => onDownload?.(file)}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#0070F3] hover:bg-[#0059c5] text-white rounded-xl text-xs font-bold shadow-md transition-colors"
                                >
                                    <span className="material-symbols-outlined text-base">download</span>
                                    Download File
                                </button>
                                {previewUrl && (
                                    <button
                                        type="button"
                                        onClick={() => window.open(previewUrl, '_blank')}
                                        className="w-full flex items-center justify-center gap-2 py-2 border border-[#c1c6d7] dark:border-[#344258] text-[#414754] dark:text-[#9aa5bc] hover:bg-[#eff4ff] dark:hover:bg-[#212f45] rounded-xl text-xs font-semibold transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-base">open_in_new</span>
                                        Open in New Tab
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // Render directly into document.body to escape any parent stacking contexts
    if (typeof document !== 'undefined') {
        return createPortal(modalContent, document.body);
    }

    return modalContent;
}
